/**
 * Team → Briefings.
 *
 * Meeting prep the AI employees filed for the owner (reviews of kind
 * `briefing`). The meeting ends a briefing, not an approval: before it the
 * owner reads and comments, after it (phase `held`) closes it with a short
 * debrief – both on the review detail page. The server closes a debrief
 * nobody touched after 3 days.
 *
 * Open view: the open briefings in groups (`lib/briefings.ts ::
 * groupBriefings`) – "Debrief open", then by the meeting's Berlin day:
 * "Today", "Next 7 days", "Later" – each in the server's order (held first,
 * then by meeting time). History: closed and withdrawn ones, newest first.
 * A dot marks a version the owner has not opened.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";

import { adminApi, type AdminReviewDoc } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { QueryBoundary } from "../../components/QueryBoundary";
import { ReviewGroup } from "../../components/review/ReviewGroup";
import { FilterChips } from "../../components/ui/FilterChips";
import { ShortcutHelp, ShortcutHint } from "../../components/ui/ShortcutHelp";
import { reviewUrl } from "../../lib/aiTeam";
import {
  briefingPhaseText, briefingStatus, groupBriefings, isOpenBriefing, isUnseen, meetingLabel,
} from "../../lib/briefings";
import { useListNavigation, type ListRowProps } from "../../lib/hooks/useListNavigation";
import { useUrlFilter } from "../../lib/hooks/useUrlFilter";
import { toggled } from "../../lib/reviewGroups";
import { submissionTitle } from "../../lib/reviewVersions";

type BriefingView = "open" | "history";

const VIEWS: { value: BriefingView; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "history", label: "History" },
];
const VIEW_VALUES = VIEWS.map((v) => v.value);

/** Open and holding a version the owner has not opened. */
const unread = (doc: AdminReviewDoc) => isOpenBriefing(doc) && isUnseen(doc);

export function BriefingsPage() {
  const [view, setView] = useUrlFilter<BriefingView>("view", VIEW_VALUES, "open");
  const briefingsQuery = adminApi.aiTeam.reviews(view === "open" ? "open" : "closed", "briefing");
  const navigate = useNavigate();
  const [nowMs] = useState(() => Date.now());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const rows = briefingsQuery.data ?? [];
  const groups = view === "open" ? groupBriefings(rows, nowMs) : [];
  // Keyboard order = visual order: the unfolded groups, or the history list.
  const navItems = view === "open" ? groups.filter((g) => !collapsed.has(g.id)).flatMap((g) => g.docs) : rows;
  const debriefs = rows.filter((d) => d.phase === "held").length;
  const unreadCount = rows.filter(unread).length;

  const nav = useListNavigation({
    items: navItems,
    getKey: (d) => d.id,
    onOpen: (d) => navigate(reviewUrl(d.id, "briefing")),
  });

  const renderRow = (doc: AdminReviewDoc) => (
    <BriefingRow key={doc.id} doc={doc} nowMs={nowMs} active={nav.isActive(doc)} linkProps={nav.rowProps(doc)} />
  );

  return (
    <>
      <PageHeader
        title="Briefings"
        description="Meeting prep the AI employees filed for you – read and comment before the meeting, close it with a short debrief after."
      />

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <FilterChips aria-label="Briefing view" options={VIEWS} value={view} onChange={setView} />
        {briefingsQuery.data ? (
          <div className="text-sm text-ink-low">
            {view === "open" ? (
              <>
                <span className="tnum font-medium text-ink">{rows.length}</span> open
                {debriefs ? ` · ${debriefs} debrief${debriefs === 1 ? "" : "s"} open` : ""}
                {unreadCount ? ` · ${unreadCount} unread` : ""}
              </>
            ) : (
              "Closed and withdrawn, newest first"
            )}
          </div>
        ) : null}
        <span className="ml-auto">
          <ShortcutHint />
        </span>
      </div>

      <QueryBoundary
        query={briefingsQuery}
        isEmpty={(list) => list.length === 0}
        empty={
          view === "open" ? (
            <div className="empty-state">
              <div className="title">No open briefings</div>
              <p className="hint">Meeting prep shows up here when an employee files it – closed ones are in History.</p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="title">Nothing here</div>
              <p className="hint">No briefing has been closed yet.</p>
            </div>
          )
        }
      >
        {() =>
          view === "open" ? (
            <div className="space-y-4">
              {groups.map((group) => {
                const groupUnread = group.docs.filter(unread).length;
                return (
                  <ReviewGroup
                    key={group.id}
                    name={group.name}
                    count={group.docs.length}
                    urgent={0}
                    oldestWaitingSeconds={null}
                    folded={collapsed.has(group.id)}
                    onToggle={() => setCollapsed((current) => toggled(current, group.id))}
                    action={groupUnread ? (
                      <span className="text-xs text-ink-low px-4 shrink-0">{groupUnread} unread</span>
                    ) : null}
                  >
                    {group.docs.map((d) => renderRow(d))}
                  </ReviewGroup>
                );
              })}
            </div>
          ) : (
            <section className="panel panel--flush">
              <ul className="divide-y divide-rule">{rows.map((d) => renderRow(d))}</ul>
            </section>
          )
        }
      </QueryBoundary>

      <ShortcutHelp open={nav.helpOpen} onClose={() => nav.setHelpOpen(false)} shortcuts={nav.shortcuts} />
    </>
  );
}

function BriefingRow({
  doc,
  nowMs,
  active,
  linkProps,
}: {
  doc: AdminReviewDoc;
  nowMs: number;
  active: boolean;
  linkProps: ListRowProps;
}) {
  const isUnread = unread(doc);
  // The "Debrief open" group already says it; a held row needs no phase line.
  const phase = doc.phase === "held" ? null : briefingPhaseText(doc, nowMs);
  const status = briefingStatus(doc);
  return (
    <li className={clsx("flex items-center gap-3 px-4 hover:bg-surface-tint", active && "row-cursor")}>
      <Link className="flex min-w-0 flex-1 items-center gap-3 py-2.5" to={reviewUrl(doc.id, "briefing")} {...linkProps}>
        <span className="w-2 shrink-0 flex justify-center">
          {isUnread ? (
            <span
              className="unread-dot"
              aria-label="Unread"
              title={doc.seen_version == null ? "Not opened yet" : `New version since you read v${doc.seen_version}`}
            />
          ) : null}
        </span>
        <span className="font-mono text-xs text-ink tnum shrink-0">{meetingLabel(doc, nowMs)}</span>
        <span className="min-w-0 flex-1">
          <span className={clsx("block text-sm text-ink truncate", isUnread && "font-semibold")}>{doc.title}</span>
          {phase ? <span className="block text-xs text-ink-low truncate">{phase}</span> : null}
        </span>
        {doc.topic ? <span className="text-xs text-ink-low shrink-0">{doc.topic.name}</span> : null}
        {doc.agent_slug ? <span className="font-mono text-xs text-ink-low shrink-0">{doc.agent_slug}</span> : null}
        {doc.content_version > 1 ? (
          <span className="text-xs text-ink-low tnum shrink-0" title={submissionTitle(doc)}>v{doc.content_version}</span>
        ) : null}
        {doc.status !== "pending" ? <Pill value={status.label} tone={status.tone} /> : null}
      </Link>
      <ShortId id={doc.id} className="shrink-0" />
    </li>
  );
}
